// Разбор и перенос параметров входа между экранами `sso.iec.exchange`.
//
// Экран входа — не начало флоу, а его середина: сервис уже отправил браузер
// сюда с OAuth-параметрами, и потерять хотя бы один из них означает, что после
// успешного входа человека некуда вернуть. Поэтому весь разбор живёт в одном
// месте, а экраны только читают готовое.
//
// Набор нестандартных параметров зафиксирован разведкой нынешнего SSO
// (`.claude/research/01-sso-contract.md` §3): страница входа обязана понимать
// `redirect_to`, `login=true`, `finish_page`, `ref` и `email_redirect_uri`
// сверх обычных `client_id` / `redirect_uri` / `response_type` / `state`.

import { ECOSYSTEM_SERVICES, type EcosystemService, findService } from "@iec-id/shared/constants";
import { z } from "zod";

/**
 * Параметры адресной строки экранов входа.
 *
 * Схема — белый список: неизвестные параметры отбрасываются. Это сознательно.
 * Authorize-URL мы собираем сами (`buildSilentAuthPayload`), поэтому «пронести
 * всё как есть» не нужно, а вот протащить чужой параметр в редирект — способ
 * получить открытый редирект чужими руками.
 */
export const authSearchSchema = z.object({
	/** Клиент OAuth. Его отсутствие означает «человек пришёл на домен сам». */
	client_id: z.string().min(1).max(255).optional(),
	/** Куда сервис ждёт код авторизации. Проверяется сервером по реестру. */
	redirect_uri: z.string().max(2048).optional(),
	/** Нынешним SSO игнорируется, но клиенты его шлют — переносим как есть. */
	response_type: z.string().max(64).optional(),
	/** CSRF-состояние клиента. Клиент ждёт его обратно байт в байт. */
	state: z.string().max(2048).optional(),
	scope: z.string().max(512).optional(),
	/**
	 * PKCE и `nonce` исходного запроса авторизации.
	 *
	 * Экран входа обязан донести их до `/api/auth/check`: человек попал сюда
	 * потому, что живой сессии не было, и код ему выдаст именно тот вызов.
	 * Пока этих полей в белом списке не было, они терялись на экране входа —
	 * клиент присылал `code_challenge`, а получал код, к которому его
	 * `code_verifier` не привязан, и PKCE переставал защищать что-либо.
	 */
	code_challenge: z.string().max(256).optional(),
	code_challenge_method: z.string().max(16).optional(),
	nonce: z.string().max(256).optional(),
	/** Куда вернуть человека ПОСЛЕ callback сервиса. */
	redirect_to: z.string().max(2048).optional(),
	/**
	 * Куда вернуть после принятия юридических документов.
	 *
	 * Ставит его НАШ сервер: `authorize` при `pendingAcceptance.blocking`
	 * отправляет человека на `/consent?return_to=<полный authorize-URL>`
	 * (декрет Д10). Без этого параметра принятие документа заканчивалось бы
	 * витриной IEC ID, а не тем сервисом, в который человек входил.
	 */
	return_to: z.string().max(2048).optional(),
	/** `true` — показать форму принудительно, даже если сессия жива. */
	login: z.enum(["true", "false"]).optional(),
	/** Прокидывается насквозь в итоговый редирект сервиса. */
	finish_page: z.string().max(2048).optional(),
	/** Код приглашения с лендинга `/ref/<code>`. */
	ref: z.string().min(3).max(32).optional(),
	/** Адрес возврата для ссылки из письма. */
	email_redirect_uri: z.string().max(2048).optional(),
	/** Явный идентификатор сервиса, если клиент его прислал. */
	service: z.string().min(2).max(64).optional(),
	/**
	 * Короткий алиас сервиса в реф-ссылке (`iec.exchange/ref/CODE?s=studyhub`).
	 * В `AuthSearch` не остаётся: `parseAuthSearch` поднимает его в `service`.
	 */
	s: z.string().min(2).max(64).optional(),
	/** Экран `/error`: код отказа и человеческое пояснение от сервера. */
	error: z.string().max(64).optional(),
	error_description: z.string().max(512).optional(),
});

export type AuthSearch = Omit<z.infer<typeof authSearchSchema>, "s">;

/**
 * `validateSearch` для роутов входа.
 *
 * Мусор в адресной строке не должен ронять экран входа: это последний экран,
 * на котором человек может что-то сделать. Поэтому `safeParse` и пустой объект
 * вместо исключения.
 */
export function parseAuthSearch(input: Record<string, unknown>): AuthSearch {
	const parsed = authSearchSchema.safeParse(input).data;
	if (!parsed) return {};
	const { s, service, ...rest } = parsed;
	if (service ?? s) return { ...rest, service: service ?? s };
	return rest;
}

/** Сессия сервиса живёт дольше вкладки: `login=true` — единственный способ заставить форму показаться. */
export function wantsExplicitForm(search: AuthSearch): boolean {
	return search.login === "true";
}

/** Человек пришёл из сервиса, а не открыл домен руками. */
export function hasOAuthContext(search: AuthSearch): boolean {
	return Boolean(search.client_id);
}

/**
 * Нужен ли экран «Продолжить как …».
 *
 * Chooser — это согласие отдать данные ЧУЖОМУ сервису и обменять сессию на
 * код OAuth. Витрина `id.iec.exchange` и админка уже видят ту же cookie на
 * `*.iec.exchange`: спрашивать «продолжить в IEC ID?» после входа в IEC ID —
 * лишний шаг. Код OAuth своим поверхностям не выдаём.
 *
 * Свой OAuth-клиент `iec-id` (если он всё же приехал в query) — тоже свои:
 * кода сервису не нужно, человека уводим на витрину.
 */
export function needsAccountChooser(search: AuthSearch): boolean {
	if (!hasOAuthContext(search)) return false;
	const service = resolveService(search);
	return service?.id !== "iec-id";
}

// ─── сервис-инициатор ───────────────────────────────────────────────────────

/**
 * Кто именно прислал человека. Три источника по убыванию надёжности:
 * явный `service`, `client_id` (в реестре IEC ID он совпадает с id сервиса)
 * и, как последний шанс, origin из `redirect_uri`.
 */
export function resolveService(search: AuthSearch): EcosystemService | undefined {
	const direct = search.service ? findService(search.service) : undefined;
	if (direct) return direct;

	const byClient = search.client_id ? findService(search.client_id) : undefined;
	if (byClient) return byClient;

	const origin = safeOrigin(search.redirect_uri);
	if (!origin) return undefined;
	return ECOSYSTEM_SERVICES.find((service) => safeOrigin(service.url) === origin);
}

// ─── защита редиректов ──────────────────────────────────────────────────────

/** Витрина IEC ID — куда человек попадает, если сервиса-инициатора нет. */
export const ID_WEB_URL = findService("iec-id")?.url ?? "https://id.iec.exchange";

const ALLOWED_HOST_SUFFIXES = [".iec.exchange", ".iec.study"] as const;
const ALLOWED_HOSTS = new Set(
	["iec.exchange", "iec.study"].concat(
		ECOSYSTEM_SERVICES.map((service) => safeHost(service.url)).filter(
			(host): host is string => host !== undefined,
		),
	),
);

/**
 * Пропускает только https-адреса экосистемы.
 *
 * `redirect_to` приходит из адресной строки, то есть от кого угодно. Сервер
 * свои редиректы валидирует сам, но адрес, который клиент собирается открыть
 * без похода на сервер, проверять больше некому.
 */
export function safeExternalUrl(raw: string | undefined): string | undefined {
	if (!raw) return undefined;
	let url: URL;
	try {
		url = new URL(raw, window.location.origin);
	} catch {
		return undefined;
	}
	if (url.protocol !== "https:" && url.hostname !== "localhost") return undefined;
	const host = url.hostname.toLowerCase();
	if (host === window.location.hostname) return url.toString();
	if (ALLOWED_HOSTS.has(host)) return url.toString();
	if (ALLOWED_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))) return url.toString();
	return undefined;
}

function safeOrigin(raw: string | undefined): string | undefined {
	if (!raw) return undefined;
	try {
		return new URL(raw).origin;
	} catch {
		return undefined;
	}
}

function safeHost(raw: string): string | undefined {
	try {
		return new URL(raw).hostname.toLowerCase();
	} catch {
		return undefined;
	}
}

/**
 * Куда уводить после успешного входа, когда сервер не назвал адрес сам.
 *
 * Порядок: `return_to` (мы сами прервали поход человека на `/consent` — вернуть
 * обязаны туда же) → явный `redirect_to` → сайт сервиса-инициатора → витрина.
 * Оба адреса проходят через `safeExternalUrl`: они пришли из адресной строки.
 */
export function fallbackDestination(search: AuthSearch): string {
	const interrupted = safeExternalUrl(search.return_to);
	if (interrupted) return interrupted;
	const explicit = safeExternalUrl(search.redirect_to);
	if (explicit) return explicit;
	const service = resolveService(search);
	return service?.url ?? ID_WEB_URL;
}

/** Адрес внутреннего экрана с сохранением всего контекста входа. */
export function localUrl(path: string, search: AuthSearch): string {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(search)) {
		if (typeof value === "string" && value.length > 0) params.set(key, value);
	}
	const query = params.toString();
	return query ? `${path}?${query}` : path;
}

// ─── тихая авторизация (raw-эндпоинт, не tRPC) ──────────────────────────────

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/**
 * Ответ `POST /api/auth/check` (DOMAIN-MODEL §13 — silent SSO).
 *
 * Эндпоинт достался в наследство от нынешнего SSO и отвечает в обёртке
 * `{success, payload}` (research §4.1), но часть клиентов читает плоскую форму.
 * Принимаем обе: это дешевле, чем ломать вход на несовпадении обёртки.
 */
const silentAuthResponseSchema = z.object({
	redirect_url: z.string().optional(),
	payload: z.object({ redirect_url: z.string().optional() }).optional(),
});

export type SilentAuthResult = { redirectUrl: string } | { redirectUrl: null };

/**
 * Обменять живую сессию на код авторизации для сервиса-инициатора.
 *
 * Это не tRPC: `/api/auth/check` — raw-эндпоинт Fastify, который IEC ID обязан
 * держать ради совместимости с нынешним фронтом входа. Экран chooser'а зовёт
 * ровно его, а не выдумывает новую процедуру.
 */
export async function requestSilentAuthorization(search: AuthSearch): Promise<SilentAuthResult> {
	if (!needsAccountChooser(search) || !search.client_id || !search.redirect_uri) {
		return { redirectUrl: null };
	}

	const body: Record<string, string> = {};
	for (const key of [
		"client_id",
		"redirect_uri",
		"response_type",
		"state",
		"redirect_to",
		"finish_page",
		"scope",
		"code_challenge",
		"code_challenge_method",
		"nonce",
	] as const) {
		const value = search[key];
		if (value) body[key] = value;
	}

	const response = await fetch(`${API_BASE}/api/auth/check`, {
		method: "POST",
		credentials: "include",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(body),
	});
	if (!response.ok) throw new Error(`auth/check ${response.status}`);

	const parsed = silentAuthResponseSchema.safeParse(await response.json());
	const url = parsed.data?.redirect_url ?? parsed.data?.payload?.redirect_url;
	return { redirectUrl: url ?? null };
}

/**
 * Незавершённый `authorize` для передачи на сервер — в том виде, в каком его
 * ждёт `auth.googleStart`.
 *
 * Уход на `accounts.google.com` — это уход с нашего домена вместе со всей
 * адресной строкой. Всё, что нужно для выдачи кода, обязано уехать в
 * подписанном state и вернуться обратно, иначе человек приезжает в сервис
 * гостем: сессия у нас есть, кода у сервиса нет.
 */
export function pendingAuthorizeOf(search: AuthSearch) {
	if (!search.client_id || !search.redirect_uri) return undefined;
	return {
		clientId: search.client_id,
		redirectUri: search.redirect_uri,
		...(search.response_type ? { responseType: search.response_type } : {}),
		...(search.state ? { state: search.state } : {}),
		...(search.scope ? { scope: search.scope } : {}),
		...(search.code_challenge ? { codeChallenge: search.code_challenge } : {}),
		...(search.code_challenge_method ? { codeChallengeMethod: search.code_challenge_method } : {}),
		...(search.nonce ? { nonce: search.nonce } : {}),
	};
}

/**
 * Куда уводить человека, который ТОЛЬКО ЧТО аутентифицировался.
 *
 * Порядок здесь — не вкусовщина, он оплачен боевым дефектом. Сервер отдаёт
 * `redirectTo` («куда девать человека»), но он не знает про OAuth-контекст
 * вкладки и в отсутствие `serviceId` честно называет витрину. Пока этот ответ
 * стоял первым, человек, вошедший из Hero или StudyHub, приземлялся на
 * `id.iec.exchange` **залогиненным у нас и гостем в сервисе**: сервис ждал
 * `code`, а браузер туда даже не заходил.
 *
 * Поэтому первым идёт доигрывание OAuth: человек пришёл из сервиса за кодом,
 * и всё остальное — фолбэки для того, кто пришёл к нам сам.
 */
export async function resolveSignedInDestination(
	search: AuthSearch,
	preferred?: string | null,
): Promise<string> {
	if (hasOAuthContext(search)) {
		try {
			const silent = await requestSilentAuthorization(search);
			if (silent.redirectUrl) return silent.redirectUrl;
		} catch {
			// Тихая авторизация — не последний рубеж: витрина примет человека и так.
		}
	}
	const explicit = safeExternalUrl(preferred ?? undefined);
	if (explicit) return explicit;
	return fallbackDestination(search);
}

// ─── адрес почты между шагами ───────────────────────────────────────────────

const PENDING_KEY = "iec-id.signin.pending";

/**
 * Что экран кода знает о только что отправленном письме.
 *
 * Хранится в `sessionStorage`, а не в адресной строке: полный адрес почты в
 * URL попадает в историю браузера, в реферер и в логи прокси — а показать его
 * нужно ровно одному человеку и ровно на одном экране.
 */
export type PendingSignIn = {
	email: string;
	maskedEmail: string;
	/**
	 * Момент, когда код перестанет действовать (мс эпохи).
	 *
	 * `null` — срок неизвестен. Так бывает ровно в одном случае: сервер ответил
	 * 429 «письмо уже улетело», и когда именно оно улетело, мы не знаем.
	 * Подставлять сюда «сейчас плюс десять минут» нельзя: экран кода показывает
	 * это число человеку и на нём же гасит поле ввода — выдуманный срок означает
	 * либо ложный «код истёк», либо ложное «ещё 9 минут» на мёртвом коде.
	 */
	expiresAt: number | null;
	/** Момент, начиная с которого разрешена повторная отправка (мс эпохи). */
	resendAt: number;
};

const pendingSchema = z.object({
	email: z.string().min(3),
	maskedEmail: z.string().min(1),
	expiresAt: z.number().int().positive().nullable(),
	resendAt: z.number().int().nonnegative(),
});

export function savePendingSignIn(pending: PendingSignIn): void {
	try {
		sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
	} catch {
		// Приватный режим Safari отдаёт QuotaExceededError на первой же записи.
		// Экран кода это переживёт: он просто уведёт обратно на ввод почты.
	}
}

export function readPendingSignIn(): PendingSignIn | undefined {
	try {
		const raw = sessionStorage.getItem(PENDING_KEY);
		if (!raw) return undefined;
		return pendingSchema.safeParse(JSON.parse(raw)).data;
	} catch {
		return undefined;
	}
}

export function clearPendingSignIn(): void {
	try {
		sessionStorage.removeItem(PENDING_KEY);
	} catch {
		// см. savePendingSignIn
	}
}

// ─── маскирование ───────────────────────────────────────────────────────────

/**
 * `maya.andersson@iec-mail.com` → `m•••n@iec-mail.com` (макет 4a).
 *
 * Нужна на клиенте только как подстраховка: маску отдаёт сервер. Но экран
 * chooser'а не имеет права показать полный адрес ни при каких обстоятельствах
 * (инвариант №5), поэтому запасной вариант обязан существовать.
 */
export function maskEmail(email: string): string {
	const at = email.lastIndexOf("@");
	if (at <= 0) return "•••";
	const local = email.slice(0, at);
	const domain = email.slice(at);
	if (local.length <= 2) return `${local.slice(0, 1)}•••${domain}`;
	return `${local.slice(0, 1)}•••${local.slice(-1)}${domain}`;
}

/** «Maya Andersson» → «Maya»: кнопка «Продолжить как …» набирает имя, а не ФИО. */
export function firstNameOf(displayName: string): string {
	return displayName.trim().split(/\s+/).filter(Boolean)[0] ?? displayName.trim();
}
